import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Loader2, Send } from 'lucide-react';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';

export default function StaffAppChatThreadPage() {
  const { threadId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { reloadFeed } = useOutletContext();
  const [messages, setMessages] = useState(null);
  const [thread, setThread] = useState(null);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    try {
      const res = await api.get(`/staff/portal/chat/threads/${threadId}/messages`);
      setMessages(res.data.messages || []);
      setThread(res.data.thread);
    } catch {
      // keep showing the last known messages on a transient poll failure
    }
  };

  useEffect(() => {
    load();
    reloadFeed(); // clears this thread's unread badge count in the shell
    const poll = setInterval(load, 8000);
    return () => clearInterval(poll);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ block: 'end' }); }, [messages]);

  const send = async () => {
    const body = draft.trim();
    if (!body) return;
    setSending(true);
    try {
      await api.post(`/staff/portal/chat/threads/${threadId}/messages`, { body });
      setDraft('');
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send that message');
    } finally { setSending(false); }
  };

  return (
    <div className="flex flex-col h-[calc(100vh-57px-64px)]" data-testid="staff-app-chat-thread">
      <div className="flex items-center gap-2 px-4 py-2.5 border-b border-border shrink-0">
        <button onClick={() => navigate('/staff-app/chats')} aria-label="Back" data-testid="staff-app-chat-back">
          <ArrowLeft className="w-4.5 h-4.5" />
        </button>
        <p className="text-sm font-bold truncate" data-testid="staff-app-chat-patient-name">{thread?.patient_name || location.state?.patientName || 'Patient conversation'}</p>
      </div>

      <div className="flex-1 overflow-y-auto px-3 py-3 space-y-2">
        {!messages ? (
          <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
        ) : messages.map((m) => (
          <div key={m.message_id} className={`flex ${m.sender === 'staff' ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm ${m.sender === 'staff' ? 'bg-primary text-primary-foreground rounded-br-sm' : 'bg-muted text-foreground rounded-bl-sm'}`} data-testid={`staff-app-chat-message-${m.message_id}`}>
              {m.body}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex items-end gap-2 px-3 py-2.5 border-t border-border shrink-0">
        <Textarea value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Reply to this patient…"
          rows={1} className="min-h-[40px] max-h-28 resize-none" data-testid="staff-app-chat-input"
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
        <Button size="icon" onClick={send} disabled={sending || !draft.trim()} data-testid="staff-app-chat-send">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}
